// Updated PdfDownloadDialog.tsx

import { PDFReportType } from '@gameonly/core';
import { Description, InsertChart } from '@mui/icons-material';
import {
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WhiteButton } from '../ui/Button';
import CustomLoadingButton from '../ui/LoadingButton';

export type PdfDownloadContext = {
  label: string;
  fetch: (reportType: PDFReportType) => Promise<Response | undefined>;
  fileName: (reportType: PDFReportType) => string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  context: PdfDownloadContext;
};

const PdfDownloadDialog = ({ open, onClose, context }: Props) => {
  const [selectedReportType, setSelectedReportType] =
    useState<PDFReportType | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const { t } = useTranslation(['project, common']);

  const downloadPdf = useCallback(
    async (reportType: PDFReportType) => {
      setIsFetching(true);
      try {
        const response = await context.fetch(reportType);
        if (!response) return;

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = context.fileName(reportType);
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        onClose();
        setSelectedReportType(null);
      } finally {
        setIsFetching(false);
      }
    },
    [context, onClose],
  );

  const handleValidate = () => {
    if (selectedReportType) {
      downloadPdf(selectedReportType);
    }
  };

  const isSelected = (type: PDFReportType) => selectedReportType === type;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{context.label}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} alignItems="stretch">
          {[PDFReportType.Simple, PDFReportType.Complete].map((type) => (
            <Grid item xs={12} sm={6} key={type}>
              <Card
                variant={isSelected(type) ? 'outlined' : undefined}
                sx={{
                  borderColor: isSelected(type) ? 'primary.main' : 'grey.300',
                  height: '100%',
                }}
              >
                <CardActionArea
                  sx={{ height: '100%' }}
                  onClick={() => setSelectedReportType(type)}
                >
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    {type === PDFReportType.Simple ? (
                      <Description fontSize="large" color="primary" />
                    ) : (
                      <InsertChart fontSize="large" color="primary" />
                    )}
                    <Typography variant="h6">
                      {t(`project:pdf.reportTypeChoice.${type}.short`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(`project:pdf.reportTypeChoice.${type}.description`)}{' '}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <WhiteButton onClick={onClose}>{t(`common:cancel`)}</WhiteButton>
        <CustomLoadingButton
          onClick={handleValidate}
          disabled={!selectedReportType || isFetching}
          startIcon={isFetching ? <CircularProgress size={18} /> : null}
        >
          {t(`common:validate`)}
        </CustomLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PdfDownloadDialog;
